"use client";
import React, { useEffect, useRef } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import Image, { StaticImageData } from "next/image";
import { AspectRatio, Box, HStack, Wrap, WrapItem } from "@chakra-ui/react";
import { useTextVariant } from "@/theme/hooks/useSectionStyle";
import { Text } from "@/elements";

export type PageProps = {
  image: StaticImageData;
  label: string;
  href: string;
};

type PagesTabsProps = {
  props: PageProps[];
};

const PagesTabs = ({ props }: PagesTabsProps) => {
  return (
    <Box>
      <MobileTabs props={props} />
      <DesktopTabs props={props} />
    </Box>
  );
};

const TabItem = ({
  data,
  isActive,
  padding,
}: {
  data: PageProps;
  isActive: boolean;
  padding: string;
}) => {
  const TextVariant = useTextVariant();
  return (
    <HStack
      borderWidth="1px"
      borderRadius="20px"
      bg={isActive ? "blue.100" : "transparent"}
      borderColor={isActive ? "black" : "gray.300"}
      _hover={{ bg: isActive ? "" : "gray.100" }}
      className="brand-shadow"
      p={padding}
      gap={2}
    >
      <AspectRatio ratio={1} position="relative" w="24px">
        <Image
          alt="image tab"
          src={data.image}
          fill
          style={{ objectFit: "contain", objectPosition: "center" }}
        />
      </AspectRatio>
      <Text
        fontWeight={600}
        variant={TextVariant}
        color={isActive ? "blue.500" : "black"}
      >
        {data.label}
      </Text>
    </HStack>
  );
};

const DesktopTabs = ({ props }: PagesTabsProps) => {
  const pathname = usePathname();

  return (
    <Wrap gap={4} align="center" justify="center" display={{ base: "none", md: "flex" }}>
      {props.map((data, index) => {
        const isActive = pathname === data.href;
        return (
          <WrapItem key={index}>
            <Link href={data.href}>
              <Box borderRadius="20px">
                <TabItem data={data} isActive={isActive} padding="16px" />
              </Box>
            </Link>
          </WrapItem>
        );
      })}
    </Wrap>
  );
};

const MobileTabs = ({ props }: PagesTabsProps) => {
  const pathname = usePathname();
  const activeTabRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (activeTabRef.current) {
      activeTabRef.current.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [pathname]);

  return (
    <Box
      className="scrollbar-hide"
      overflowX="auto"
      whiteSpace="nowrap"
      mb={-8}
      bg="white"
      borderColor="gray.200"
      display={{ base: "block", md: "none" }}
      css={{
        scrollSnapType: "x mandatory",
        WebkitOverflowScrolling: "touch", // camelCase, no dash
      }}
    >
      <HStack gap={3} w="max-content">
        {props.map((data, index) => {
          const isActive = pathname === data.href;
          return (
            <Link key={index} href={data.href}>
              <Box
                borderRadius="20px"
                css={{
                  scrollSnapAlign: "center",
                  minWidth: "150px",
                  display: "inline-flex",
                  justifyContent: "center",
                }}
                ref={isActive ? activeTabRef : null}
              >
                <TabItem data={data} isActive={isActive} padding="8px" />
              </Box>
            </Link>
          );
        })}
      </HStack>
    </Box>
  );
};

export default PagesTabs;
